import { Controller } from '@hotwired/stimulus'

// Firma online de la LOPD (ENH-RNK-105). Firma de Firmafy embebida en un modal
// <dialog> full-screen. El CTA hace POST a `firma-lopd-online` y monta un
// iframe en la zona del modal con la `iframe_url` de la respuesta — SIEMPRE el
// **Widget** (PDF rellenable + pre-fill; contrato jul-2026: Firmafy entrega la
// página de firma como iframe, ya no existe widget.js/FirmafyWidget.init).
// Con 2 titulares los datos del cotitular viajan en el POST y se persisten;
// firmará por email cuando Firmafy soporte el 2º firmante en el widget.
//
// El aspa/ESC cierra el modal SIN matar el proceso: el CTA se re-habilita y
// puede reabrirse. El polling a `firma-lopd-estado` detecta `titular_firmada`
// (1 titular) o `lopd_completa` (ambos), cierra el modal y muestra "firmada ✓".
//
// El widget además habla por postMessage: FIRMAFY_SIGNED al completar la firma
// (mostramos el estado optimista "firma recibida" y adelantamos la consulta de
// estado — la verdad sigue siendo el webhook→BD, nunca un mensaje del
// navegador; si la confirmación no llega en pendienteTimeoutMs se restaura el
// CTA) y FIRMAFY_CLOSED cuando el firmante pulsa el aspa propia de la página
// de Firmafy (cerramos nuestro modal, el proceso sigue reabrible).
//
// Si Firmafy falla (503), se muestra un error y queda el camino de siempre:
// enviar la documentación y firmar por email (aviso_lopd_pendiente).
//
// Donde se eligen los titulares (`confirmarUnTitular`), con uno solo se pregunta
// antes de abrir la firma: el documento solo lleva a quien firma y después no se
// puede añadir al cotitular. Al terminar la firma se avisa a la página
// (eventos `recibida`/`firmada`, y `restaurada` si no llega a confirmarse) para
// que fije los titulares.
export default class extends Controller {
  static targets = ['boton', 'modal', 'zona', 'ok', 'error', 'errorCotitular', 'pendiente',
    'confirmacionUnTitular', 'avisoCotitular', 'emailCotitular']

  static values = {
    sesionUrl: String,
    estadoUrl: String,
    // Aviso al servidor de que el titular terminó la firma: Firmafy nos avisa
    // del cierre minutos después, y si el titular envía en ese hueco no hay que
    // crearle otra firma.
    recibidaUrl: String,
    // A dónde llevar al firmante en cuanto la firma queda registrada. Lo usa
    // firmar-lopd, donde la documentación YA está enviada: su copy («Ya puedes
    // enviar tu documentación») y su vía manual dejan de tener sentido en
    // cuanto firma. Es el mismo destino al que redirige el servidor si recarga
    // (`HipotecasFirmaLopdController#lopd_ya_firmada?`). Vacío en la pantalla
    // de subida, donde el firmante sigue en la página para enviar.
    redirigirUrl: String,
    pollIntervalMs: { type: Number, default: 4000 },
    // Tope de sondeos (~15 min a 4s): la firma se completa en minutos y la sesión
    // de Firmafy caduca en 1h; sin tope, un modal olvidado abierto sondearía sin
    // fin. Al agotarse solo paramos — el CTA sigue reabrible (feedback #7).
    maxPolls: { type: Number, default: 225 },
    // Cuánto mantener el estado optimista "firma recibida" sin confirmación del
    // webhook antes de restaurar el CTA (para no dejar el bloque muerto).
    pendienteTimeoutMs: { type: Number, default: 120000 },
    confirmarUnTitular: { type: Boolean, default: false }
  }

  connect () {
    this.onMensajeFirmafy = (event) => {
      if (!event.origin.endsWith('.firmafy.net') && !event.origin.endsWith('.firmafy.com')) return
      if (event.data?.type === 'FIRMAFY_SIGNED') this.firmaRecibida()
      if (event.data?.type === 'FIRMAFY_CLOSED') this.cerrarModal()
    }
    window.addEventListener('message', this.onMensajeFirmafy)
  }

  disconnect () {
    window.removeEventListener('message', this.onMensajeFirmafy)
    this.pararPolling()
    this.pararTimeoutPendiente()
  }

  async firmar () {
    if (this.hayQueConfirmarUnTitular()) return this.pedirConfirmacionUnTitular()

    this.botonTarget.disabled = true
    this.ocultar(this.errorTarget)
    this.ocultar(this.errorCotitularTarget)

    const dosTitulares = this.numTitulares() >= 2
    if (dosTitulares && !this.datosCotitularCompletos()) {
      this.mostrar(this.errorCotitularTarget)
      this.botonTarget.disabled = false
      return
    }

    try {
      const respuesta = await fetch(this.sesionUrlValue, {
        method: 'POST',
        headers: { 'X-CSRF-Token': this.csrfToken(), Accept: 'application/json' },
        body: this.parametrosFirma(dosTitulares)
      })
      if (!respuesta.ok) throw new Error(`firma online ${respuesta.status}`)

      const datos = await respuesta.json()
      const url = datos.iframe_url
      if (!url) throw new Error('firma online sin URL de firma')

      this.abrirModalIframe(url)
      this.empezarPolling()
    } catch {
      this.mostrar(this.errorTarget)
      this.botonTarget.disabled = false
    }
  }

  firmarComoUnicoTitular () {
    this.unicoTitularConfirmado = true
    this.cerrarConfirmacionUnTitular()
    this.firmar()
  }

  // Son dos: se añade el titular en el stepper y se le lleva a sus datos, que
  // hay que rellenar antes de firmar.
  anadirTitular () {
    this.cerrarConfirmacionUnTitular()
    document.querySelector('[data-hipotecas-doc-titulares-target="btnIncrementar"]')?.click()
    document.querySelector('input[name="cotitular_nombre"]')?.focus()
  }

  cerrarModal () {
    this.modalTarget.close()
  }

  // `close` del <dialog>: llega tanto por el aspa como por ESC. El proceso de
  // firma sigue vivo (feedback #7): re-habilitamos el CTA para poder reabrir y
  // el polling continúa por si el firmante ya había completado la firma.
  modalCerrado () {
    this.botonTarget.disabled = false
  }

  // ---- privado ----

  hayQueConfirmarUnTitular () {
    return this.confirmarUnTitularValue && this.hasConfirmacionUnTitularTarget &&
      !this.unicoTitularConfirmado && this.numTitulares() === 1
  }

  pedirConfirmacionUnTitular () {
    this.ocultar(this.botonTarget)
    this.mostrar(this.confirmacionUnTitularTarget)
  }

  cerrarConfirmacionUnTitular () {
    this.ocultar(this.confirmacionUnTitularTarget)
    this.mostrar(this.botonTarget)
  }

  // Con dos titulares, a Firmafy le toca ahora mandar su email al cotitular:
  // se explica qué pasa al enviar, con el email que acaba de dar.
  avisarDelCotitular () {
    if (!this.hasAvisoCotitularTarget || this.numTitulares() < 2) return
    if (this.hasEmailCotitularTarget) this.emailCotitularTarget.textContent = this.campoCotitular('cotitular_email')
    this.mostrar(this.avisoCotitularTarget)
  }

  // El firmante completó la firma en el widget (postMessage FIRMAFY_SIGNED).
  // Cerramos nuestro modal (la pantalla de éxito de Firmafy ya no ofrece cómo
  // salir, y sin nuestra aspa el usuario quedaría atrapado) y mostramos el
  // estado optimista "✓ Firma registrada. Ya puedes enviar…" sin CTA en la
  // página — pero NO marcamos nada en firme: la confirmación real llega por
  // webhook→BD vía consultarEstado/polling. Si no llega en pendienteTimeoutMs,
  // restauramos el CTA para que el bloque no quede muerto.
  firmaRecibida () {
    if (this.modalTarget.open) this.modalTarget.close()
    const aviso = this.avisarFirmaRecibida()
    // El aviso se espera antes de navegar: al salir de la página el fetch se
    // cancelaría y el servidor tendría que enterarse solo por el webhook.
    if (this.redirigirUrlValue) return aviso.finally(() => this.irAlDestino())
    this.consultarEstado()
    if (this.hasPendienteTarget) {
      this.ocultar(this.botonTarget)
      this.mostrar(this.pendienteTarget)
    }
    this.avisarDelCotitular()
    this.dispatch('recibida')
    this.pararTimeoutPendiente()
    this.timeoutPendiente = setTimeout(() => this.restaurarCta(), this.pendienteTimeoutMsValue)
  }

  async avisarFirmaRecibida () {
    if (!this.recibidaUrlValue) return
    try {
      await fetch(this.recibidaUrlValue, {
        method: 'POST',
        headers: { 'X-CSRF-Token': this.csrfToken(), Accept: 'application/json' }
      })
    } catch {
      // Sin el aviso, el servidor se entera igualmente por el cierre de Firmafy.
    }
  }

  // Mientras el servidor tenga la firma por recibida (esperando el cierre de
  // Firmafy, que llega con minutos de retraso), el bloque sigue en «firma
  // recibida» y no vuelve a ofrecer «Firmar».
  mantenerPendiente () {
    this.pararTimeoutPendiente()
    this.ocultar(this.botonTarget)
    if (this.hasPendienteTarget) this.mostrar(this.pendienteTarget)
  }

  restaurarCta () {
    this.pararTimeoutPendiente()
    if (this.hasPendienteTarget) this.ocultar(this.pendienteTarget)
    if (this.hasAvisoCotitularTarget) this.ocultar(this.avisoCotitularTarget)
    this.mostrar(this.botonTarget)
    this.botonTarget.disabled = false
    this.dispatch('restaurada')
  }

  pararTimeoutPendiente () {
    if (this.timeoutPendiente) clearTimeout(this.timeoutPendiente)
    this.timeoutPendiente = null
  }

  parametrosFirma (dosTitulares) {
    const params = new FormData()
    params.append('num_titulares', this.numTitulares())
    if (dosTitulares) {
      params.append('cotitular_nombre', this.campoCotitular('cotitular_nombre'))
      params.append('cotitular_email', this.campoCotitular('cotitular_email'))
      // El teléfono es opcional: viaja vacío si no se rellenó y el backend lo
      // persiste con `presence` (nil). El documento de identidad es obligatorio (lo valida el backend).
      params.append('cotitular_dni', this.campoCotitular('cotitular_dni'))
      params.append('cotitular_telefono', this.campoCotitular('cotitular_telefono'))
    }
    return params
  }

  // El stepper vive en otro controller (hipotecas-doc-titulares); leemos su
  // contador igual que hace hipotecas-doc-acordeon.
  numTitulares () {
    const contador = document.querySelector('[data-hipotecas-doc-titulares-target="contador"]')
    const n = contador ? parseInt(contador.textContent, 10) : 1
    return Number.isNaN(n) ? 1 : n
  }

  campoCotitular (name) {
    const input = document.querySelector(`input[name="${name}"]`)
    return input ? input.value.trim() : ''
  }

  datosCotitularCompletos () {
    // El documento de identidad, el que sea y sin validar (producto, 7-oct-2026).
    return ['cotitular_nombre', 'cotitular_email', 'cotitular_dni'].every(campo => this.campoCotitular(campo) !== '')
  }

  // La página de firma del Widget se embebe como iframe full-screen dentro
  // del modal.
  abrirModalIframe (url) {
    const iframe = document.createElement('iframe')
    iframe.src = url
    iframe.className = 'rnk-HipotecasDoc_LopdOnlineIframe'
    iframe.setAttribute('allow', 'camera') // la firma avanzada puede pedir cámara
    this.zonaTarget.replaceChildren(iframe)
    this.modalTarget.showModal()
  }

  empezarPolling () {
    this.pararPolling()
    this.pollCount = 0
    this.pollTimer = setInterval(() => {
      if (++this.pollCount > this.maxPollsValue) return this.pararPolling()
      this.consultarEstado()
    }, this.pollIntervalMsValue)
  }

  pararPolling () {
    if (this.pollTimer) clearInterval(this.pollTimer)
    this.pollTimer = null
  }

  async consultarEstado () {
    try {
      const respuesta = await fetch(this.estadoUrlValue, { headers: { Accept: 'application/json' } })
      if (!respuesta.ok) return

      const datos = await respuesta.json()
      if (datos.titular_firmada || datos.lopd_completa) this.marcarFirmada()
      else if (datos.firma_recibida) this.mantenerPendiente()
    } catch {
      // Error de red transitorio: el siguiente tick reintenta.
    }
  }

  marcarFirmada () {
    this.pararPolling()
    this.pararTimeoutPendiente()
    if (this.modalTarget.open) this.modalTarget.close()
    // Camino sin postMessage (el navegador lo perdió): lo confirma el polling.
    if (this.redirigirUrlValue) return this.irAlDestino()
    if (this.hasPendienteTarget) this.ocultar(this.pendienteTarget)
    this.ocultar(this.botonTarget)
    this.mostrar(this.okTarget)
    this.avisarDelCotitular()
    // El fallback manual deja de tener sentido: la firma ya está en marcha/registrada.
    this.dispatch('firmada')
  }

  irAlDestino () {
    this.pararPolling()
    this.pararTimeoutPendiente()
    window.location.assign(this.redirigirUrlValue)
  }

  csrfToken () {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }

  mostrar (el) { el.hidden = false }

  ocultar (el) { el.hidden = true }
}
